import { en } from '@/i18n/en';
import { ru } from '@/i18n/ru';
import type { MessageKey, Messages, Params, PluralForms } from '@/i18n/types';

/** Russian first: it is the default, and the order drives every picker. */
export const LOCALES = ['ru', 'en'] as const;

export type Locale = (typeof LOCALES)[number];

const DICTIONARIES: Record<Locale, Messages> = { en, ru };

const STORAGE_KEY = 'selflapse:locale';

/**
 * What the app falls back to whenever nobody has said otherwise: a visitor
 * whose browser asks for a language we do not ship, and every surface that
 * cannot vary by locale at all — the document title, the PWA manifest, the
 * notification a push with no body has to invent.
 */
export const DEFAULT_LOCALE: Locale = 'ru';

function isLocale(value: string | null): value is Locale {
  return LOCALES.includes(value as Locale);
}

/**
 * The locale the browser asks for, or Russian.
 *
 * Matches on the language subtag alone: `ru-RU`, `ru-BY` and `ru` all want the
 * same interface, and nothing here varies by region.
 */
export function detectLocale(): Locale {
  for (const tag of navigator.languages ?? [navigator.language]) {
    const language = tag.split('-')[0]?.toLowerCase() ?? '';

    if (isLocale(language)) {
      return language;
    }
  }

  return DEFAULT_LOCALE;
}

/**
 * A guest's own choice, if they made one.
 *
 * Read synchronously — hence `localStorage` rather than IndexedDB — because the
 * first paint happens before any of it could be awaited, and a screen that
 * flashes English before correcting itself is worse than no memory at all.
 */
export function storedLocale(): Locale | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    return isLocale(stored) ? stored : null;
  } catch {
    // Private-mode Safari throws on access rather than returning null.
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not remembering the choice is survivable; failing to render is not.
  }
}

let active: Locale = DEFAULT_LOCALE;

/**
 * The locale outside the React tree.
 *
 * `apiFetch` needs it for `Accept-Language` and is not a component, so the
 * provider mirrors its state here rather than every caller threading a locale
 * through. There is exactly one writer, which keeps the two from diverging.
 */
export function activeLocale(): Locale {
  return active;
}

export function setActiveLocale(locale: Locale): void {
  active = locale;
}

function selectForm(locale: Locale, forms: PluralForms, count: number): string {
  // `PluralForms` declares exactly the categories `select` can return, so the
  // lookup needs no cast — only a fallback for the ones a locale leaves out.
  return forms[new Intl.PluralRules(locale).select(count)] ?? forms.other;
}

function interpolate(template: string, params: Params | undefined): string {
  if (!params) {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];

    return value === undefined ? whole : String(value);
  });
}

export function translate(locale: Locale, key: MessageKey, params?: Params): string {
  const entry = DICTIONARIES[locale][key];

  if (typeof entry === 'string') {
    return interpolate(entry, params);
  }

  // Plural entries are driven by `n` by convention; without it there is no
  // count to select on and `other` is the only defensible form.
  const count = Number(params?.n ?? 0);

  return interpolate(selectForm(locale, entry, count), params);
}

export function isMessageKey(value: string): value is MessageKey {
  return value in en;
}
